import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

export default function Footer({ config }) {
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative bg-black text-white border-t border-white/10 pt-14 md:pt-28 pb-32 md:pb-16"
      data-testid="site-footer"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Wordmark */}
        <div className="mb-12 md:mb-24">
          <div className="font-serif text-[16vw] md:text-[10vw] leading-[0.85] tracking-tight text-white">
            She Cuts<span className="italic text-zinc-500">.</span>
          </div>
          <div className="eyebrow mt-3 md:mt-4 text-zinc-500">
            Barber Studio · Est. {config.established} · Atlanta
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-10 border-t border-white/10 pt-14">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow text-zinc-500 mb-4">Studio</div>
            <div className="flex items-start gap-3 text-zinc-300">
              <MapPin className="w-4 h-4 mt-1" strokeWidth={1.4} />
              <div>
                <div>{config.address_line_1}</div>
                <div>{config.address_line_2}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <a
                href={`tel:${config.phone_tel}`}
                className="flex items-center gap-3 text-zinc-300 line-hover w-fit"
                onClick={() => trackEvent("call_clicked", { source: "footer" })}
                data-testid="footer-call"
              >
                <Phone className="w-4 h-4" strokeWidth={1.4} />
                {config.phone}
              </a>
              <a
                href={`mailto:${config.email}`}
                className="flex items-center gap-3 text-zinc-300 line-hover w-fit"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" strokeWidth={1.4} />
                {config.email}
              </a>
            </div>
          </div>

          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow text-zinc-500 mb-4">Explore</div>
            <ul className="space-y-2">
              {[
                { l: "Services", to: "/services" },
                { l: "Photos",   to: "/photos" },
                { l: "Founder",  to: "/founder" },
                { l: "Reviews",  to: "/reviews" },
                { l: "Visit",    to: "/visit" },
              ].map((it) => (
                <li key={it.to}>
                  <Link to={it.to} className="text-zinc-300 line-hover">
                    {it.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow text-zinc-500 mb-4">Act</div>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/book"
                  className="text-zinc-300 line-hover"
                  data-testid="footer-book"
                >
                  Book
                </Link>
              </li>
              <li>
                <a
                  href={config.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 line-hover"
                >
                  Directions
                </a>
              </li>
              <li>
                <a
                  href={
                    config.reviews_platform_url ||
                    "https://m.yelp.com/biz/shecuts-barber-studio-atlanta"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 line-hover"
                  data-testid="footer-yelp"
                >
                  Yelp Reviews
                </a>
              </li>
              <li>
                <a
                  href={config.voyageatl_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 line-hover"
                >
                  VoyageATL
                </a>
              </li>
              <li>
                <a
                  href={config.yoili_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 line-hover"
                >
                  YOILI Designs
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow text-zinc-500 mb-4">Follow</div>
            <div className="flex items-center gap-3">
              <a
                href={config.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.4} />
              </a>
              <a
                href={config.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.4} />
              </a>
            </div>

            <div className="mt-8 font-serif italic text-2xl leading-snug text-zinc-300">
              “More than a haircut.”
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="eyebrow text-zinc-600">
            © {year} {config.business_name} · Owned by {config.owner_name}
          </div>
          <div className="eyebrow text-zinc-600">
            Designed with craft in Atlanta
          </div>
        </div>

        <div
          className="mt-16 md:mt-20 flex justify-center"
          data-testid="footer-airotech-credit"
        >
          <p className="text-[11px] tracking-[0.24em] uppercase text-zinc-500 text-center whitespace-nowrap font-mono">
            website designed by{" "}
            <a
              href="https://airotech.io/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AIROTECH — website designer (opens in new tab)"
              className="text-zinc-300 hover:text-white underline-offset-4 hover:underline transition-colors"
              data-testid="footer-airotech-link"
            >
              AIROTECH
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
